import { fetchAPI, API_BASE_URL } from "./apiCore";
import { getToken } from "./auth";
import { appendCsvQueryParam } from "./apiQuery";


// New unified sample collection API - GET /sample-collection/encounters/{encounterId}/plan
export const getEncounterPlan = async (encounterId) => {
  console.log(`📤 Getting encounter plan: GET /sample-collection/encounters/${encounterId}/plan`);
  return fetchAPI(`biobank/sample-collection/encounters/${encodeURIComponent(encounterId)}/plan`, "GET");
};

// New unified sample collection API - POST /sample-collection/encounters/{encounterId}/batches
export const registerNewBatch = async (encounterId, kitType, kitNumber, studyIds = []) => {
  console.log(`📤 Registering new batch: POST /sample-collection/encounters/${encounterId}/batches`, { kitType, kitNumber, studyIds });
  return fetchAPI(
    `biobank/sample-collection/encounters/${encodeURIComponent(encounterId)}/batches`,
    "POST",
    {},
    { kitType, kitNumber, studyIds }
  );
};

// New unified sample collection API - POST /sample-collection/batches/{batchId}/validate
export const validateCollectionBatch = async (batchId, encounterId, studyId) => {
  console.log(`📤 Validating collection batch: POST /sample-collection/batches/${batchId}/validate`, { encounterId, studyId });
  return fetchAPI(
    `biobank/sample-collection/batches/${encodeURIComponent(batchId)}/validate`,
    "POST",
    {},
    { encounterId, studyId },
  );
};

// New unified sample collection API - POST /sample-collection/encounters/{encounterId}/collect
export const collectSamples = async (encounterId, batchId, samples) => {
  console.log(`📤 Collecting samples: POST /sample-collection/encounters/${encounterId}/collect`, { batchId, samples });
  return fetchAPI(
    `biobank/sample-collection/encounters/${encodeURIComponent(encounterId)}/collect`,
    "POST",
    {},
    { batchId, samples },
  );
};

// COLLECTION KITS
export const fetchCollectionKits = async () => {
  try {
    const result = await fetchAPI("biobank/collection-batches/paged?page=0&size=200", "GET");
    return Array.isArray(result?.content) ? result.content : [];
  } catch (error) {
    console.error("Error fetching collection kits:", error);
    return [];
  }
};


// Fetch collection kits with server-side pagination and real-time collection status
export const fetchCollectionKitsPaged = async (page = 0, size = 20, kitId = null, kitType = null) => {
  try {
    let endpoint = `biobank/collection-batches/paged?page=${page}&size=${size}`;
    if (kitId) {
      endpoint += `&batchId=${kitId}`;
    }
    if (kitType) {
      endpoint += `&kitType=${kitType}`;
    }
    // Backend now sorts by kitNumber ASC by default

    console.log(`🌐 API: Fetching paged collection kits - ${endpoint}`);
    const result = await fetchAPI(endpoint, "GET");
    return result;
  } catch (error) {
    console.error("❌ API: Error fetching paged collection kits:", error);
    return { content: [], totalElements: 0 };
  }
};


export const validateCollectionKit = async (kitId) => {
  try {
    return await fetchAPI(`biobank/collection-batches/${kitId}`, "GET");
  } catch (error) {
    // If kit doesn't exist, API will return 404, which is expected for validation
    if (error.response && error.response.status === 404) {
      return null; // Kit doesn't exist, which means it's valid to create
    }
    console.error("Error validating collection kit:", error);
    throw error;
  }
};


export const createCollectionKit = async (kitType, kitNumber) => {
  try {
    const token = await getToken();
    if (!token) throw new Error("No authentication token available");

    const response = await fetch(`${API_BASE_URL}/biobank/collection-batches`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({
        kitType,
        kitNumber,
      }),
    });

    if (!response.ok) {
      const resClone = response.clone();
      let errorMessage;
      try {
        const errJson = await resClone.json();
        errorMessage = errJson.message || JSON.stringify(errJson);
      } catch {
        errorMessage = await resClone.text();
      }
      throw new Error(
        errorMessage || `Error ${response.status}: ${response.statusText}`
      );
    }

    return await response.json();
  } catch (error) {
    console.error("Error creating collection kit:", error);
    throw error;
  }
};


// ALIQUOT PROCESSING

export const fetchSampleById = async (sampleId) => {
  try {
    return await fetchAPI(
      `biobank/samples/${encodeURIComponent(sampleId)}`,
      "GET"
    );
  } catch (error) {
    console.error("Error fetching sample by ID:", error);
    return null;
  }
};


export const fetchSamplesPaged = async ({
  page = 0,
  size = 25,
  unprocessedOnly = false,
  sampleTypes = [],
  qualities = [],
} = {}) => {
  try {
    const params = new URLSearchParams({
      page: String(page),
      size: String(size),
      unprocessedOnly: String(!!unprocessedOnly),
    });
    appendCsvQueryParam(params, "sampleTypes", sampleTypes);
    appendCsvQueryParam(params, "qualities", qualities);

    return await fetchAPI(`biobank/samples/paged?${params.toString()}`, "GET");
  } catch (error) {
    console.error("Error fetching paged samples:", error);
    return {
      content: [],
      totalElements: 0,
      totalPages: 0,
      pageNumber: page,
      pageSize: size,
    };
  }
};


export const fetchUniqueAliquotBarcodes = async (status = null) => {
  try {
    let url = "biobank/aliquots/barcodes";
    if (status) {
      url += `?status=${encodeURIComponent(status)}`;
    }
    return await fetchAPI(url, "GET");
  } catch (error) {
    console.error("Error fetching unique aliquot barcodes:", error);
    return [];
  }
};


export const fetchAliquotsPaged = async ({
  page = 0,
  size = 25,
  status = null,
  sampleTypes = [],
  qualities = [],
  batchIds = [],
  alzheimersStatuses = [],
} = {}) => {
  try {
    const params = new URLSearchParams({
      page: String(page),
      size: String(size),
    });
    if (status) {
      params.set("status", status);
    }
    appendCsvQueryParam(params, "sampleTypes", sampleTypes);
    appendCsvQueryParam(params, "qualities", qualities);
    appendCsvQueryParam(params, "batchIds", batchIds);
    appendCsvQueryParam(params, "alzheimersStatuses", alzheimersStatuses);

    return await fetchAPI(`biobank/aliquots/paged?${params.toString()}`, "GET");
  } catch (error) {
    console.error("Error fetching paged aliquots:", error);
    return {
      content: [],
      totalElements: 0,
      totalPages: 0,
      pageNumber: page,
      pageSize: size,
    };
  }
};


export const fetchSampleTypes = async () => {
  try {
    const sampleTypes = await fetchAPI("biobank/sample-types", "GET");
    // Add id property for compatibility
    sampleTypes.forEach((element) => (element.id = element.sampleLetter));
    return sampleTypes;
  } catch (error) {
    console.error("Error fetching sample types:", error);
    return [];
  }
};


// Fetch kit type options for dropdown
export const fetchKitTypeOptions = async () => {
  try {
    const kitTypes = await fetchAPI("biobank/options/kit-types", "GET");
    return kitTypes;
  } catch (error) {
    console.error("Error fetching kit type options:", error);
    return [];
  }
};


export const updateSampleStorage = async (
  sampleId,
  { aliquotStorage, storageComments }
) => {
  return await fetchAPI(
    `biobank/samples/${encodeURIComponent(sampleId)}`,
    "PATCH",
    {},
    { aliquotStorage, storageComments }
  );
};

export const processSample = async (sampleId, payload) => {
  return await fetchAPI(
    `biobank/sample-processing/samples/${encodeURIComponent(sampleId)}/process`,
    "POST",
    {},
    payload
  );
};


// Fetch sample activity timeline
export const fetchSampleActivity = async (sampleId) => {
  try {
    return await fetchAPI(
      `biobank/samples/${encodeURIComponent(sampleId)}/activity`,
      "GET"
    );
  } catch (error) {
    console.error("Error fetching sample activity:", error);
    return [];
  }
};


export const postAliquotCheckoutComplete = async (payload) => {
  try {
    return await fetchAPI('biobank/aliquots/checkout/complete', 'POST', {}, payload);
  } catch (error) {
    console.error('Error completing checkout:', error);
    throw error;
  }
};


export const fetchUsageRecords = (params = {}) => fetchAPI('biobank/usage-records', 'GET', params);
export const createUsageRecord = (payload) => fetchAPI('biobank/usage-records', 'POST', {}, payload);
export const fetchShipmentRecords = (params = {}) => fetchAPI('biobank/shipments', 'GET', params);
export const fetchShipmentAliquots = (shipmentId) =>
  fetchAPI(`biobank/shipments/${encodeURIComponent(shipmentId)}/aliquots`, 'GET');
